import React, { useEffect } from 'react';

import { ProductOptions } from '@storefront/types/Product';
import useGetProductsInformations from '@storefront/hooks/useGetProductsInformations';
import { getRegularPrice } from '@storefront/utils/priceHandler';
import { getShortDescription } from '@storefront/utils/prodRequests';

import { SelectionButton } from '../../selection-button/selection-button';
import {
  configurationState,
  removeOptions,
  updateConfigurations,
} from '../../state-management/pdp-config-state';
import { saveProductConfiguration } from '../../utils/utils';
import {
  BundleItemOptionWithLowestPrice,
  BundleItemWithLowestPrice,
  BundleProductWithLowestPrice,
  handlePriceDisplay,
} from '../product-configuration.utils';
import { useScrollContext } from '../scroll-context';
import {
  GetPdpStateDocument,
  GetPdpStateQuery,
} from '@storefront/data-commerce';
import { useApolloClient } from '@apollo/client';
import {
  calculateDeltaPrice,
  removePaperOptions,
} from './options-columns.utils';

export type OptionsColumnsProps = {
  option: BundleItemOptionWithLowestPrice;
  skuList: string[];
  bundleOptionUid: string;
  requiredOption?: boolean;
  index?: number;
  isUIDSelected?: boolean;
  parentId?: string;
  hidden?: boolean;
  zeroDollarUid?: string;
  bundleProduct?: BundleProductWithLowestPrice;
};
export default function OptionsColumns(props: OptionsColumnsProps) {
  const {
    option,
    skuList,
    bundleOptionUid,
    requiredOption = false,
    index,
    isUIDSelected = false,
    parentId,
    hidden = false,
    zeroDollarUid = '',
    bundleProduct,
  } = props;

  const { configuration, activeSKU, bundleOptions } =
    useGetProductsInformations(skuList);
  const { scrollNonRequiredOption, scrollRequiredOption } = useScrollContext();
  const handleScroll = requiredOption
    ? scrollRequiredOption
    : scrollNonRequiredOption;

  const client = useApolloClient();
  const {
    pdpConfig: { prices, selected_options },
  } = client.readQuery<GetPdpStateQuery>({
    query: GetPdpStateDocument,
    variables: { skuList, configuration },
  });

  const handleChangeOption =
    (uid: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const updateValue = event.target.value;

      // Remove paper when selecting another print plan
      const configs = (configuration[activeSKU] ?? {}) as ProductOptions;
      removePaperOptions(configs, bundleOptions);

      updateConfigurations({
        configuration,
        activeSKU,
        uid,
        updateValue,
        requiredOption,
      });
      handleScroll(parentId);
    };

  const handleRemoveOption = () => {
    removeOptions({
      configuration,
      activeSKU,
      uid: bundleOptionUid,
      zeroDollarUid,
    });
    handleScroll(parentId);
  };

  useEffect(() => {
    const selectedOption = (configuration?.[activeSKU] as ProductOptions)?.[
      bundleOptionUid
    ];
    if (activeSKU && !selectedOption && option.is_default) {
      const newState = {
        ...configuration,
        [activeSKU]: {
          [bundleOptionUid]: option.uid,
        },
      };
      configurationState(newState);
      saveProductConfiguration(newState);
    }
  }, [configuration, activeSKU]);

  const optionPrice = () => {
    const { price } = handlePriceDisplay(bundleProduct, option);

    return calculateDeltaPrice({
      bundleOptions,
      prices,
      selected_options,
      price,
    });
  };

  const isChecked =
    (configuration?.[activeSKU] as ProductOptions)?.[bundleOptionUid] ===
    option.uid;

  if (hidden) return null;
  return requiredOption ? (
    <SelectionButton
      key={option.uid}
      inputTestId={option.uid}
      checked={isChecked}
      popular={option.hp_is_popular === '1'}
      title={option.product.name}
      name={option.product.name}
      isDisabled={!activeSKU}
      selectedPaperOption={
        (configuration?.[activeSKU] as ProductOptions)?.[
          'selectedPaperOption'
        ] === 'true'
      }
      onChange={handleChangeOption(bundleOptionUid)}
      value={option.uid}
      id={option.product.name}
      bullets={index + 1}
      description={getShortDescription(option)}
      price={optionPrice()}
      data-testid="required-option-price"
    />
  ) : (
    <SelectionButton
      key={option.uid}
      inputTestId={option.uid}
      checked={isChecked}
      popular={option.hp_is_popular === '1'}
      title={option.product.name}
      name={option.product.name}
      isDisabled={!activeSKU || !isUIDSelected}
      onChange={handleChangeOption(bundleOptionUid)}
      value={option.uid}
      id={option.product.name}
      onRemove={handleRemoveOption}
      optional // will render 2 radio buttons, to enable/disable option
      description={getShortDescription(option)}
      price={getRegularPrice(option)}
      selectedPaperOption={
        sessionStorage.getItem('selectedPaperOption') === 'true'
      }
    />
  );
}
